import { useRef } from 'react';
import { useButton, useMenuTrigger } from 'react-aria';
import { useMenuTriggerState } from 'react-stately';
import { Theme as MuiTheme } from '@mui/material/styles';
import { useTheme } from '@emotion/react';

// local imports
import HorizontalSeparator from 'components/common/HorizontalSeperator';
import SlideInMenu from './SlideInMenu';
import SvgChevronRight from '../Icons/small-chevron-right.svg';
import SvgChevronLeft from '../Icons/small-chevron-left.svg';
import { MainMenuItemProps } from './MainMenuItem';
import { themeColor } from 'services/styleUtils/colorUtils';
import { doSignOut, filterNavigationLinks } from '../SiteHeaderUtils';
import { SIGN_OUT_TEXT } from '../SiteHeaderConstants';
import { useAsyncMemo } from 'services/hooks/useAsyncMemo';

const MobileMenuSection = (props: MainMenuItemProps) => {
  const { section, portalRef, session } = props;
  const theme = useTheme() as MuiTheme;
  const buttonRef = useRef(null);

  const state = useMenuTriggerState({});

  const { menuTriggerProps, menuProps } = useMenuTrigger(
    {
      type: 'menu',
    },
    state,
    buttonRef,
  );

  const { buttonProps } = useButton(menuTriggerProps, buttonRef);

  const [links] = useAsyncMemo(
    async () =>
      await filterNavigationLinks(section?.fields.links ?? [], session, true),
    [section, session],
    null,
    `MobileMenuSection-${section?.contentID}`, // Cache key
  );

  // if account section and user logged in, use the menu title
  // otherwise use the mobile menu title if one is present
  const title =
    section?.fields?.isAccountSection === 'true' && session?.user
      ? section?.fields?.menuTitle
      : (section?.fields?.mobileMenuTitle ?? section?.fields?.menuTitle);

  return (
    <>
      <button
        {...buttonProps}
        ref={buttonRef}
        type="button"
        className="flex items-center text-left py-2 text-blue font-semibold font-serif"
      >
        <span className="flex-auto">{title}</span>
        <SvgChevronRight className="flex-none text-2xl stroke-blue" />
      </button>

      <SlideInMenu
        {...menuProps}
        className="flex flex-col bg-white absolute z-[1299] inset-0"
        isOpen={state.isOpen}
        portalContainer={portalRef?.current}
        autoFocus={false}
      >
        <div className="flex-none">
          <div className="py-2 w-full mx-auto px-4 md:px-6 lg:px-10 max-w-[1392px]">
            <button
              className="flex items-center text-left py-2 w-full"
              onClick={() => state.close()}
            >
              <SvgChevronLeft className="flex-none text-2xl stroke-blue mr-2" />
              <span className="flex-auto text-blue text-lg font-semibold font-serif">
                {title}
              </span>
            </button>

            <HorizontalSeparator className="mt-2" />
          </div>
        </div>
        <div className="flex-auto overflow-auto overscroll-contain">
          <div className="py-2 w-full mx-auto px-4 md:px-6 lg:px-10 max-w-[1392px]">
            <div className="grid">
              <div className="group/section flex flex-col">
                <div className="grid text-sm pb-2 mb-2 ">
                  {links?.map((link) => {
                    return (
                      <div key={link.contentID} className="py-2">
                        {link.fields?.link?.text?.toLowerCase() ===
                          SIGN_OUT_TEXT && <HorizontalSeparator />}
                        <a
                          style={{
                            color: themeColor(
                              theme,
                              link.fields?.textColor?.fields?.color ??
                                'primary.main',
                            ),
                          }}
                          key={link.contentID}
                          target={link?.fields?.link?.target}
                          title={link?.fields?.link?.text}
                          className="py-2"
                          onClick={async (e) => {
                            if (
                              link.fields?.link?.text?.toLowerCase() ===
                                SIGN_OUT_TEXT &&
                              session
                            ) {
                              e.preventDefault();
                              e.stopPropagation();
                              await doSignOut(session);
                            }
                          }}
                          href={
                            link.fields?.link?.text?.toLowerCase() ===
                            SIGN_OUT_TEXT
                              ? '#'
                              : link.fields.link.href
                          }
                        >
                          {link?.fields?.link?.text}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SlideInMenu>
    </>
  );
};

export default MobileMenuSection;
